import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  zohoBooksService,
  type ZohoEstimate,
  type EstimatePrefill,
} from '../../services/zohoBooksService';

interface ZohoEstimateImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  // If the wizard already knows which Zoho customer this contract is for,
  // pre-filter the estimate list to that customer's estimates.
  customerExternalId?: string;
  onImported: (prefill: EstimatePrefill, estimateNumber?: string) => void;
}

export function ZohoEstimateImportModal({
  isOpen,
  onClose,
  customerExternalId,
  onImported,
}: ZohoEstimateImportModalProps) {
  const [estimates, setEstimates] = useState<ZohoEstimate[]>([]);
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isOpen) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, customerExternalId]);

  const load = async (searchTerm: string = '') => {
    setLoading(true);
    try {
      const { estimates } = await zohoBooksService.listEstimates({
        customerId: customerExternalId,
        search: searchTerm,
      });
      setEstimates(estimates);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load estimates');
      setEstimates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void load(search.trim());
  };

  const handleImport = async (est: ZohoEstimate) => {
    setImportingId(est.estimate_id);
    const label = est.estimate_number || est.subject || est.estimate_id;
    const toastId = toast.loading(`Importing ${label}…`);
    try {
      const result = await zohoBooksService.importEstimate(est.estimate_id);
      const count = result.prefill.deliverables?.length ?? 0;
      toast.success(
        `Imported ${label}${count > 0 ? ` — ${count} deliverable${count === 1 ? '' : 's'}` : ''}`,
        { id: toastId },
      );
      onImported(result.prefill, result.estimate_number);
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Import failed', { id: toastId });
    } finally {
      setImportingId(null);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Start from a Zoho Books Estimate
                    </Dialog.Title>
                    <p className="mt-0.5 text-xs text-gray-500">
                      Selecting an estimate pre-fills the wizard. You can still edit everything before saving.
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleSearch} className="border-b border-gray-200 px-6 py-3">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by estimate number, subject, or customer…"
                        className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                    >
                      Search
                    </button>
                  </div>
                </form>

                <div className="max-h-[60vh] overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center gap-2 px-6 py-16 text-sm text-gray-500">
                      <ArrowPathIcon className="h-5 w-5 animate-spin" />
                      Loading estimates…
                    </div>
                  ) : estimates.length === 0 ? (
                    <div className="px-6 py-16 text-center text-sm text-gray-500">
                      No estimates found{customerExternalId ? ' for this client' : ''}.
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {estimates.map((e) => {
                        const title = e.subject || e.estimate_number || 'Untitled estimate';
                        const total =
                          typeof e.total === 'number'
                            ? `${e.currency_symbol ?? '$'}${e.total.toLocaleString()}`
                            : '';
                        const meta = [
                          e.estimate_number,
                          e.customer_name,
                          e.date,
                          total,
                          e.status,
                        ]
                          .filter(Boolean)
                          .join(' · ');
                        return (
                          <li
                            key={e.estimate_id}
                            className="flex items-center justify-between gap-4 px-6 py-3 hover:bg-gray-50"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-gray-900">{title}</p>
                              {meta && <p className="truncate text-xs text-gray-500">{meta}</p>}
                            </div>
                            <button
                              onClick={() => handleImport(e)}
                              disabled={importingId === e.estimate_id}
                              className="shrink-0 rounded-md border border-primary-600 px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:opacity-50"
                            >
                              {importingId === e.estimate_id ? 'Importing…' : 'Import'}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
